import React, { useEffect, useState } from "react";
import {
  FormAutocomplete,
  formClasses,
  labelClasses,
  LabeledAutocomplete,
} from "../styles/AutocompleteStyles";
import { TextField } from "@mui/material";
import { responseType } from "../utils/formTypes";
import { useLazyQuery } from "@apollo/client";

import useUrlParams from "../customHooks/useUrlParams";
import { FETCH_MORE_DATA, ROWS_PER_PAGE, debouncedDelay } from "../utils/constants";
import { OrderDirection } from "../utils/commonQueryTypes";
import {
  getModifiedScrollHeight,
  handleMUISelectEvent,
} from "../utils/UtilFunctions";
import { GetStockManufacturers } from "../components/modules/asset/masters/manufacturer/queries/queries";
import {
  AtsStckManufacturerEdge,
  AtsStckManufacturerOrderField,
  GetStockManufacturersResult,
  GetStockManufacturersVars,
} from "../components/modules/asset/masters/manufacturer/queries/types";
import { useDebounce } from "../customHooks/useDebounce";
interface Props {
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  onChange: (newValue: responseType) => void;
  value: number;
  label: string;
}
const ItemManufacturer = ({ onChange, onKeyDown, label, value }: Props) => {

  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [categories, setCategories] = useState<AtsStckManufacturerEdge[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<responseType[]>([]);
  const [autoValue, setAutoValue] = useState<responseType | null>(null);

  const [GetManufacturers, { data, loading, fetchMore }] = useLazyQuery<
    GetStockManufacturersResult,
    GetStockManufacturersVars
  >(GetStockManufacturers);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetStockManufacturers.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = categories.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setCategories(updatedNewData);
        setCategoryOptions(
          updatedNewData.map(({ node }) => ({
            label: node.manufacturer,
            value: node.id,
          }))
        );
      } else {
        setCategories(newData);
        setCategoryOptions(
          newData.map(({ node }) => ({
            label: node.manufacturer,
            value: node.id,
          }))
        );
      }
      setEndCursor(data.GetStockManufacturers.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
     
        if ( customer_id) {
          GetManufacturers({
            variables: {
              after: null,
              customer_id,
              first: ROWS_PER_PAGE,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckManufacturerOrderField.MANUFACTURER,
              },
           
              name: debouncedSearch,
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [customer_id, GetManufacturers, debouncedSearch]);

  useEffect(() => {
    if (value) {
      const foundValue = categoryOptions.find(
        ({ value: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, categoryOptions]);
  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetStockManufacturers.edges;
            const pageInfo = fetchMoreResult.GetStockManufacturers.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck =
              prevResult.GetStockManufacturers.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetStockManufacturers: {
                edges: [...categories, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetStockManufacturers.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete =
    label.length > 0 ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={categoryOptions}
      onKeyDown={handleMUISelectEvent}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as responseType);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      )}
    />
  );
};

export default ItemManufacturer;
